import type { WorkTreeModel } from "@/models/WorkTreeModel";

export interface WorkerMessage {
  id: string;
  type: string;
  data?: unknown;
  error?: string;
  success: boolean;
  isLoading?: boolean;
  processingTime?: number;
}

export interface FilterTreeDataRequest {
  treeData: WorkTreeModel[];
  searchTerm: string;
  statusFilter: string;
}

export interface SortTreeDataRequest {
  treeData: WorkTreeModel[];
}

export interface IsTaskCompleteRequest {
  workItem: WorkTreeModel;
}

export interface SetCompleteStatusIdRequest {
  statusId: string | null;
}

export interface WorkerOperationResult<T> {
  data: T;
  isLoading: boolean;
  processingTime?: number;
}

class DataProcessorWorker {
  private worker: Worker | null = null;
  private messageId = 0;
  private completeStatusId: string | null = null;
  private pendingMessages = new Map<
    string,
    {
      resolve: (value: unknown) => void;
      reject: (error: Error) => void;
      onLoading?: (isLoading: boolean) => void;
    }
  >();

  constructor() {
    this.initializeWorker();
  }

  private initializeWorker() {
    if (typeof Worker !== "undefined") {
      try {
        // Dynamic worker creation to avoid Turbopack static analysis
        const createWorker = () => {
          // webpackIgnore: true - tells bundler to ignore this import
          return new Worker(
            new URL("../workers/data-processor.worker.js", import.meta.url),
            { type: "module" },
          );
        };
        this.worker = createWorker();

        this.worker.onmessage = (e: MessageEvent<WorkerMessage>) => {
          const { id, type, data, error, success, isLoading, processingTime } =
            e.data;

          if (type === "WORKER_READY") {
            console.log("Data processor worker is ready");
            return;
          }

          // Log performance metrics
          if (processingTime && processingTime > 50) {
            console.log(
              `Worker operation ${type} took ${processingTime.toFixed(2)}ms`,
            );
          }

          const pending = this.pendingMessages.get(id);
          if (pending) {
            // Handle loading state updates
            if (isLoading !== undefined && pending.onLoading) {
              pending.onLoading(isLoading);
            }

            // Only resolve/reject when operation is complete
            if (
              !isLoading &&
              (type.endsWith("_RESULT") || type.endsWith("_ERROR"))
            ) {
              this.pendingMessages.delete(id);

              if (success) {
                pending.resolve(data);
              } else {
                pending.reject(new Error(error || "Unknown worker error"));
              }
            }
          }
        };

        this.worker.onerror = (error) => {
          console.error("Worker error:", error);
          // Reject all pending messages
          this.pendingMessages.forEach(({ reject }) => {
            reject(new Error("Worker error occurred"));
          });
          this.pendingMessages.clear();
        };
      } catch (error) {
        console.warn(
          "Failed to initialize worker, falling back to main thread:",
          error,
        );
        this.worker = null;
      }
    }
  }

  private async sendMessage<T>(
    type: string,
    data?: unknown,
    onLoading?: (isLoading: boolean) => void,
  ): Promise<T> {
    if (!this.worker) {
      throw new Error(
        "Worker not available, falling back to main thread processing",
      );
    }

    const id = `msg_${++this.messageId}`;

    return new Promise<T>((resolve, reject) => {
      this.pendingMessages.set(id, {
        resolve: resolve as (value: unknown) => void,
        reject,
        onLoading,
      });

      if (this.worker) {
        this.worker.postMessage({
          id,
          type,
          data,
        });
      } else {
        reject(new Error("Worker not available"));
      }

      // Timeout after 10 seconds
      setTimeout(() => {
        if (this.pendingMessages.has(id)) {
          this.pendingMessages.delete(id);
          reject(new Error("Worker message timeout"));
        }
      }, 10000);
    });
  }

  async setCompleteStatusId(statusId: string | null): Promise<void> {
    this.completeStatusId = statusId;
    try {
      await this.sendMessage("SET_COMPLETE_STATUS_ID", { statusId });
    } catch (error) {
      console.warn("Failed to set complete status ID in worker:", error);
    }
  }

  async clearCache(): Promise<void> {
    try {
      await this.sendMessage("CLEAR_CACHE");
    } catch (error) {
      console.warn("Failed to clear cache in worker:", error);
    }
  }

  async filterTreeData(
    treeData: WorkTreeModel[],
    searchTerm: string,
    statusFilter: string,
    onLoading?: (isLoading: boolean) => void,
  ): Promise<WorkTreeModel[]> {
    try {
      return await this.sendMessage<WorkTreeModel[]>(
        "FILTER_TREE_DATA",
        {
          treeData,
          searchTerm,
          statusFilter,
        },
        onLoading,
      );
    } catch (error) {
      console.warn(
        "Failed to filter data in worker, falling back to main thread:",
        error,
      );
      // Fallback to main thread processing
      return this.fallbackFilterTreeData(treeData, searchTerm, statusFilter);
    }
  }

  async sortTreeData(
    treeData: WorkTreeModel[],
    onLoading?: (isLoading: boolean) => void,
  ): Promise<WorkTreeModel[]> {
    try {
      return await this.sendMessage<WorkTreeModel[]>(
        "SORT_TREE_DATA",
        {
          treeData,
        },
        onLoading,
      );
    } catch (error) {
      console.warn(
        "Failed to sort data in worker, falling back to main thread:",
        error,
      );
      // Fallback to main thread processing
      return this.fallbackSortTreeData(treeData);
    }
  }

  async isTaskComplete(workItem: WorkTreeModel): Promise<boolean> {
    try {
      return await this.sendMessage<boolean>("IS_TASK_COMPLETE", { workItem });
    } catch (error) {
      console.warn(
        "Failed to check task completion in worker, falling back to main thread:",
        error,
      );
      // Fallback to main thread processing
      return this.fallbackIsTaskComplete();
    }
  }

  // Fallback methods for when worker is not available
  private fallbackFilterTreeData(
    treeData: WorkTreeModel[],
    searchTerm: string,
    statusFilter: string,
  ): WorkTreeModel[] {
    // Simple fallback implementation
    const hasSearchFilter = searchTerm.trim().length > 0;
    const hasStatusFilter = statusFilter !== "all";

    if (!hasSearchFilter && !hasStatusFilter) {
      return treeData;
    }

    const searchLower = hasSearchFilter ? searchTerm.toLowerCase() : "";

    const isCompleted = (item: WorkTreeModel): boolean =>
      this.completeStatusId != null && item.statusId === this.completeStatusId;
    const isStartDateInPast = (startDate: string | null | undefined): boolean => {
      if (!startDate) return false;
      const start = new Date(startDate);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      start.setHours(0, 0, 0, 0);
      return start < today && !Number.isNaN(start.getTime());
    };
    const isStartDateInFutureOrNull = (
      startDate: string | null | undefined,
    ): boolean => {
      if (!startDate) return true;
      const start = new Date(startDate);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      start.setHours(0, 0, 0, 0);
      return !Number.isNaN(start.getTime()) && start >= today;
    };
    const matchesStatus = (item: WorkTreeModel): boolean => {
      if (statusFilter === "incomplete") {
        return this.completeStatusId === null
          ? item.statusId === null
          : item.statusId === null ||
            item.statusId !== this.completeStatusId;
      }
      if (statusFilter === "overdue") {
        return isStartDateInPast(item.startDate) && !isCompleted(item);
      }
      if (statusFilter === "upcoming") {
        return isStartDateInFutureOrNull(item.startDate) && !isCompleted(item);
      }
      return item.statusId === statusFilter;
    };

    const filterRecursive = (items: WorkTreeModel[]): WorkTreeModel[] => {
      return items
        .filter((item) => {
          const nameMatches =
            !hasSearchFilter ||
            (item.name?.toLowerCase().includes(searchLower) ?? false);

          const statusMatches =
            !hasStatusFilter || matchesStatus(item);

          const hasMatchingChildren =
            item.subRows &&
            item.subRows.length > 0 &&
            filterRecursive(item.subRows).length > 0;

          return (nameMatches && statusMatches) || hasMatchingChildren;
        })
        .map((item) => ({
          ...item,
          subRows: item.subRows ? filterRecursive(item.subRows) : undefined,
        }));
    };

    return filterRecursive(treeData);
  }

  private fallbackSortTreeData(treeData: WorkTreeModel[]): WorkTreeModel[] {
    return [...treeData].sort((a, b) => {
      const aStartDate = a.actualStartDate || a.tentativeStartDate;
      const bStartDate = b.actualStartDate || b.tentativeStartDate;

      if (!aStartDate && !bStartDate) {
        return (a.name || "").localeCompare(b.name || "");
      }
      if (!aStartDate) return 1;
      if (!bStartDate) return -1;

      const dateComparison =
        new Date(aStartDate).getTime() - new Date(bStartDate).getTime();
      if (dateComparison !== 0) {
        return dateComparison;
      }

      return (a.name || "").localeCompare(b.name || "");
    });
  }

  private fallbackIsTaskComplete(): boolean {
    // This would need the complete status ID, but for fallback we'll return false
    return false;
  }

  destroy() {
    if (this.worker) {
      this.worker.terminate();
      this.worker = null;
    }
    this.pendingMessages.clear();
  }
}

// Create singleton instance
export const dataProcessorWorker = new DataProcessorWorker();
